import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import {
  AssetClass,
  CommonErrors,
  User,
  USER_PUBLIC_ID,
  UserApproval,
  UserApprovedRequest,
  UserPublicProfile,
  UserVerificationPlatform,
  VerificationData,
} from '@monorepo/types';
import { BaseResponse } from '../core/base-entity-service';
import CrudService from '../core/crud-service';

/**
 *
 */
export default class UserService extends CrudService {
  static override route = 'user';

  /**
   *
   */
  async getUserVerificationData() {
    const response = await this.httpService.get<BaseResponse<VerificationData>>(
      `${this.path}/verification`,
    );

    return response.data;
  }

  /**
   *
   */
  async uploadProfilePicture(file: FormData) {
    const response = await this.httpService.post<FormData, { data: string }>(
      `${this.path}/upload-profile-image`,
      file,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  }

  /**
   *
   */
  async deleteProfilePicture() {
    const response = await this.httpService.delete<BaseResponse<string>>(
      `${this.path}/delete-profile-image`,
    );
    return response.data;
  }

  /**
   *
   */
  async isValidUsername(username: string) {
    const response = await this.httpService.get<BaseResponse<boolean>>(
      `${this.path}/username/valid`,
      {
        params: { username },
      },
    );
    return response.data;
  }

  /**
   *
   */
  async updateUser(userDataToUpdate: Partial<User>) {
    try {
      const processedData = { ...userDataToUpdate };
      // Convert birthDate to YYYY-MM-DD string to avoid timezone issues
      if (processedData.birthDate) {
        processedData.birthDate = dayjs(processedData.birthDate).format(
          'YYYY-MM-DD',
        );
      }

      const response = await this.httpService.put<
        Partial<User>,
        { data: Partial<User> }
      >(`${this.path}`, processedData);
      return response.data as User;
    } catch (e) {
      console.error(`Failed update user details with error: ${e}`);

      if (e instanceof AxiosError) {
        throw e.response?.data.message[0];
      }
      if (e instanceof Error) {
        throw new Error(CommonErrors.SOMETHING_WRONG);
      }
    }
  }

  /**
   *
   */
  async getKycAccessToken() {
    try {
      const response = await this.httpService.get<BaseResponse<string>>(
        `${this.path}/verification/token/${UserVerificationPlatform.SumSub}`,
      );
      return response.data;
    } catch (e) {
      console.error('Error getting KYC access token:', e);
      throw new Error('Could not get kyc access token for kyc process');
    }
  }

  async getPublicProfile(publicId?: string) {
    try {
      let url = `${this.path}/profile`;

      if (publicId) {
        url += `?${USER_PUBLIC_ID}=${publicId}`;
      }

      const response =
        await this.httpService.get<BaseResponse<UserPublicProfile>>(url);

      return response.data;
    } catch (e) {
      console.error('Error fetching public profile:', e);
      throw new Error('Failed getting profile');
    }
  }

  /**
   *
   */
  async userApproved(approvalType: UserApproval) {
    try {
      const url = `${this.path}/user-approved`;

      const response = await this.httpService.post<
        UserApprovedRequest,
        BaseResponse<void>
      >(url, { approvalType });

      return response.data;
    } catch (e) {
      console.error('Error approving user:', e);
      throw new Error('Failed user approval');
    }
  }

  async getUser(): Promise<User> {
    try {
      const response = await this.httpService.get<BaseResponse<User>>(
        `${this.path}`,
      );
      return response.data;
    } catch (e) {
      console.error('Error fetching user data:', e);
      throw new Error('Failed getting user data');
    }
  }

  /**
   * Update privacy opt-out preference
   */
  async updatePrivacyOptOut(
    doNotSellOrShare: boolean,
    userData?: {
      firstName?: string;
      lastName?: string;
      zipCode?: string;
      state?: string;
      email?: string;
    },
  ): Promise<User> {
    const response = await this.httpService.put<
      {
        doNotSellOrShare: boolean;
        firstName?: string;
        lastName?: string;
        zipCode?: string;
        state?: string;
        email?: string;
      },
      { message: string; data: User }
    >(`${this.path}/privacy-opt-out`, {
      doNotSellOrShare,
      ...userData,
    });
    return response.data;
  }

  async updateOnboardingPreference(selectedAsset: AssetClass[]): Promise<User> {
    try {
      const response = await this.httpService.put<
        { selectedAsset: AssetClass[] },
        { message: string; data: User }
      >(`${this.path}/onboarding-preference`, {
        selectedAsset,
      });
      return response.data;
    } catch (e) {
      console.error('Error updating onboarding preference:', e);
      if (e instanceof AxiosError) {
        throw e.response?.data.message[0];
      }
      throw new Error(CommonErrors.SOMETHING_WRONG);
    }
  }
}
